/**
 * URL utilities for whitelabel-aware URL construction
 * Ensures URLs are built based on the current host/environment instead of hardcoded domains
 */

/**
 * Get the base URL of the current application
 * @returns {string} The current origin (protocol + hostname + port)
 */
export const getAppBaseUrl = () => {
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  return 'https://siki.io'; // fallback for SSR
};

/**
 * List of known siki.io domains that should be treated as internal
 */
const SIKI_DOMAINS = [
  'siki.io',
  'www.siki.io',
  'staging.siki.io',
  'dev.siki.io',
  'test.siki.io',
  'memehouse.siki.io',
  'memehouse.staging.siki.io',
  'memehouse.dev.siki.io',
  'localhost',
];

/**
 * Firebase Dynamic Links domain - these redirect to siki.io but should be treated as internal
 */
const DYNAMIC_LINK_DOMAINS = [
  'sikiapp.page.link',
];

/**
 * Check if a URL is a Firebase Dynamic Link
 * @param {string} url - The URL to check
 * @returns {boolean} True if the URL is a Firebase Dynamic Link
 */
export const isDynamicLink = (url) => {
  if (!url) return false;

  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname.toLowerCase();
    return DYNAMIC_LINK_DOMAINS.some(domain => hostname === domain);
  } catch {
    return false;
  }
};

/**
 * Check if a URL is an internal siki/memehouse URL
 * @param {string} url - The URL to check
 * @returns {boolean} True if the URL is internal
 */
export const isInternalUrl = (url) => {
  if (!url) return false;

  // Relative URLs are internal
  if (url.startsWith('/') && !url.startsWith('//')) {
    return true;
  }

  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname.toLowerCase();

    // Check if it's a Firebase Dynamic Link (these redirect to siki.io)
    if (DYNAMIC_LINK_DOMAINS.some(domain => hostname === domain)) {
      return true;
    }

    // Check if it's a known siki domain
    return SIKI_DOMAINS.some(domain =>
      hostname === domain || hostname.endsWith(`.${domain}`)
    );
  } catch {
    // If URL parsing fails, treat as internal path
    return true;
  }
};

/**
 * Extract the path from a URL (including search params and hash)
 * @param {string} url - The URL to extract path from
 * @returns {string} The path portion of the URL
 */
export const extractPath = (url) => {
  if (!url) return '/';

  // Already a relative path
  if (url.startsWith('/') && !url.startsWith('//')) {
    return url;
  }

  try {
    const urlObj = new URL(url);
    return `${urlObj.pathname}${urlObj.search}${urlObj.hash}`;
  } catch {
    return url;
  }
};

/**
 * Build an app URL that respects the current whitelabel environment
 * - External URLs (not siki.io) are returned as-is
 * - Relative paths are prefixed with current origin
 * - siki.io URLs are converted to use current origin
 *
 * @param {string} path - The URL or path to build
 * @returns {string} The constructed URL
 */
export const buildAppUrl = (path) => {
  if (!path) return getAppBaseUrl();

  // External URLs (not siki.io related) - return as-is
  if (!isInternalUrl(path)) {
    return path;
  }

  // Get the path portion
  const extractedPath = extractPath(path);

  // Build full URL with current origin
  return `${getAppBaseUrl()}${extractedPath.startsWith('/') ? '' : '/'}${extractedPath}`;
};

/**
 * Get the internal navigation path for React Router
 * Use this when you want to navigate within the app using history.push()
 *
 * @param {string} url - The URL to convert to internal path
 * @returns {string} The path for React Router navigation
 */
export const getInternalPath = (url) => {
  if (!url) return '/';

  // Already a relative path
  if (url.startsWith('/') && !url.startsWith('//')) {
    return url;
  }

  // Extract path from absolute URL
  return extractPath(url);
};

/**
 * Check if the current environment is production
 * @returns {boolean} True if running in production
 */
export const isProductionEnvironment = () => {
  if (typeof window === 'undefined') return false;

  const hostname = window.location.hostname.toLowerCase();

  // Production is any .siki.io domain that's not staging/dev/test/localhost
  return (
    hostname.endsWith('.siki.io') &&
    !hostname.includes('staging') &&
    !hostname.includes('dev') &&
    !hostname.includes('test')
  ) || hostname === 'siki.io' || hostname === 'www.siki.io';
};

/**
 * Resolve a Firebase Dynamic Link to get the actual destination URL
 * For sikiapp.page.link URLs, we need to follow the redirect to get the real path
 *
 * @param {string} url - The dynamic link URL
 * @returns {Promise<string>} The resolved internal path
 */
export const resolveDynamicLink = async (url) => {
  if (!isDynamicLink(url)) {
    return getInternalPath(url);
  }

  try {
    // Try to fetch the dynamic link to get the redirect location
    // We use a HEAD request with redirect: 'manual' to get the Location header
    const response = await fetch(url, {
      method: 'HEAD',
      redirect: 'manual',
    });

    // Get the redirect location
    const location = response.headers.get('location');

    if (location) {
      // Extract the path from the redirect location
      // This will be something like https://siki.io/app/artist/123
      return getInternalPath(location);
    }
  } catch (error) {
    console.warn('Failed to resolve dynamic link:', error);
  }

  // Fallback: if we can't resolve, just go to home
  return '/';
};

/**
 * Handle a banner/link click with proper whitelabel support
 * This handles dynamic links, internal URLs, and external URLs appropriately
 *
 * @param {string} url - The URL to navigate to
 * @param {object} history - React Router history object
 * @param {function} onExternal - Optional callback for external URLs
 */
export const handleLinkClick = async (url, history, onExternal) => {
  if (!url || url === '#') return;

  // For dynamic links, resolve and navigate internally
  if (isDynamicLink(url)) {
    try {
      const internalPath = await resolveDynamicLink(url);
      history.push(internalPath);
    } catch (error) {
      console.error('Error handling dynamic link:', error);
      // Fallback to opening the link directly
      if (onExternal) {
        onExternal(url);
      } else {
        window.open(url, '_blank');
      }
    }
    return;
  }

  // For internal URLs, navigate with React Router
  if (isInternalUrl(url)) {
    const internalPath = getInternalPath(url);
    history.push(internalPath);
    return;
  }

  // For external URLs, open in new tab
  if (onExternal) {
    onExternal(url);
  } else {
    window.open(url, '_blank');
  }
};

export default {
  getAppBaseUrl,
  buildAppUrl,
  isInternalUrl,
  isDynamicLink,
  extractPath,
  getInternalPath,
  isProductionEnvironment,
  resolveDynamicLink,
  handleLinkClick,
};
